'use es6';

import tinymce from 'tinymce';
import { getPropertiesDataSensitivityMap } from 'rich-text-lib/utils/buildProperties';
import { getIsPropertyOnlyToken, getTagUseFromCategory, createTokenString } from 'rich-text-lib/utils/properties';
import { getTokenPattern, getPropertyOnlyTokenPattern, getTokenPatternWithDefault } from './utils';
import { addTokenTransforms, renderTokenBase, getTokenProperties, splitHublVar, getPlaceholderLabel, generateInnerTokenWithTextNode } from '../utils/token';
import { escapeUnescapedSingleQuote } from 'tinymce-plugins/utils/stringTransform';
import { getHsPersonalizeSettings } from '../utils';
import { addPreProcessTransform, replaceNodes } from '../utils/transformer';
const createTokenLink = ({
  href,
  text,
  style
}) => {
  const anchor = document.createElement('a');
  anchor.setAttribute('href', href);
  anchor.setAttribute('target', '_blank');
  anchor.setAttribute('style', style);
  anchor.textContent = text;
  return anchor;
};

// properties to image
const renderToken = personalizeConfig => properties => {
  const {
    property,
    category,
    subProperty,
    defaultValue,
    filter
  } = properties;
  const tagUse = getTagUseFromCategory(category);
  const isPropertyOnlyToken = getIsPropertyOnlyToken(category, property);
  const dataSensitivityMap = getPropertiesDataSensitivityMap(personalizeConfig);
  const hasDataSensitivity = dataSensitivityMap && dataSensitivityMap[`${category}.${property}`] === true;

  // NOTE: Label could be undefined at first load in which case we just
  // render the raw token information while omitting [data-mce-poperty-label]
  // so that we know which tokens need to be adjusted once we have the labels
  const label = getPlaceholderLabel({
    category,
    property,
    subProperty
  }, personalizeConfig);
  const tokenText = label || `${property}${subProperty ? `.${subProperty}` : ''}`;
  const setTokenAttributes = token => {
    token.setAttribute('data-mce-hubspot-object', 'true');
    token.setAttribute('data-mce-property-category', isPropertyOnlyToken ? 'property-only' : category);
    token.setAttribute('data-mce-property-name', property);
    if (subProperty) {
      token.setAttribute('data-mce-subproperty', subProperty);
    }
    if (label) {
      token.setAttribute('data-mce-property-label', label);
    }
    if (defaultValue != null) {
      token.setAttribute('data-mce-property-default-value', defaultValue);
    }
    if (filter) {
      token.setAttribute('data-mce-filter', filter);
    }
    if (hasDataSensitivity) {
      token.setAttribute('data-mce-data-sensitivity', true);
    }
    token.classList.add(tagUse);
  };
  return renderTokenBase(tokenText, category, setTokenAttributes, hasDataSensitivity);
};

/**
 *
 * @see MergeTagPlaceholderRewriter.java
 */
const getLegacyNodePreProcessTransform = personalizeConfig => dom => {
  const linkTypes = ['unsubscribe', 'viewaswebpage', 'emailoptin'];
  const getLinkTypeForNode = node => {
    return linkTypes.find(type => node.hasAttribute(`data-${type}`));
  };
  const nodes = Array.from(dom.querySelectorAll('input.hubspot-mergetag'));
  replaceNodes(nodes, node => {
    if (node.hasAttribute('data-canspam')) {
      const properties = getTokenProperties(node.getAttribute('data-canspam'));
      if (properties) {
        return renderToken(personalizeConfig)(properties);
      }
    } else if (getLinkTypeForNode(node)) {
      const type = getLinkTypeForNode(node);
      if (type) {
        return createTokenLink({
          text: node.getAttribute('value'),
          href: node.getAttribute(`data-${type}`),
          style: node.getAttribute('data-style')
        });
      }
    } else if (node.hasAttribute('value')) {
      const properties = splitHublVar(`contact.${node.getAttribute('value')}`);
      if (properties) {
        return renderToken(personalizeConfig)(properties);
      }
    }
    return node;
  });
};
export const replacePlaceholderWithHubl = personalizeConfig => node => {
  const category = node.getAttribute('data-mce-property-category');
  const property = node.getAttribute('data-mce-property-name');
  const subProperty = node.getAttribute('data-mce-subproperty');
  const defaultValueAttr = node.getAttribute('data-mce-property-default-value');
  const filter = node.getAttribute('data-mce-filter');
  const defaultValue = defaultValueAttr && tinymce.html.Entities.decode(defaultValueAttr);
  let prefix = '';
  const {
    customObjectPrefixes
  } = personalizeConfig || {};
  if (customObjectPrefixes) {
    const prefixKey = category === 'property-only' ? property : category;
    prefix = customObjectPrefixes && customObjectPrefixes[prefixKey] || '';
  }
  const textTokenNode = document.createTextNode(createTokenString({
    prefix,
    category,
    property,
    subProperty,
    defaultValue: defaultValue ? escapeUnescapedSingleQuote(defaultValue) : defaultValue,
    filter
  }));
  return generateInnerTokenWithTextNode(node, textTokenNode);
};
const getTokenPreProcessTransform = personalizeConfig => properties => renderToken(personalizeConfig)(properties).outerHTML;
export const addTransforms = editor => {
  const personalizeConfig = getHsPersonalizeSettings(editor);
  let prefixes = {};
  let supportedCrmObjectFQNs = [];
  if (personalizeConfig) {
    prefixes = personalizeConfig.customObjectPrefixes;
    supportedCrmObjectFQNs = personalizeConfig.supportedCrmObjectFQNs;
  }

  // Preprocessor for legacy "merge tags"
  addPreProcessTransform(editor, getLegacyNodePreProcessTransform(personalizeConfig));

  // Pre and Post transforms
  addTokenTransforms(editor, supportedCrmObjectFQNs, prefixes, [{
    TOKEN_PATTERN: getTokenPattern(personalizeConfig),
    USES_DEFAULT: false,
    PROPERTY_ONLY: false
  }, {
    TOKEN_PATTERN: getPropertyOnlyTokenPattern(personalizeConfig),
    USES_DEFAULT: false,
    PROPERTY_ONLY: true
  }, {
    TOKEN_PATTERN: getTokenPatternWithDefault(personalizeConfig),
    USES_DEFAULT: true,
    PROPERTY_ONLY: false
  }], getTokenPreProcessTransform(personalizeConfig), replacePlaceholderWithHubl(personalizeConfig));
};

/**
 * Replace token values with translated labels
 * Updating already transformed tokens with translated values
 */
export const handlePropertiesLoaded = editor => {
  const personalizeConfig = getHsPersonalizeSettings(editor);
  const body = editor.getBody();
  if (body) {
    const alreadyRenderedTokens = Array.from(body.querySelectorAll('[data-mce-property-name]:not([data-mce-property-label]):not([data-mce-rss-link]):not([data-mce-property-category=post])'));
    alreadyRenderedTokens.forEach(token => {
      const property = token.getAttribute('data-mce-property-name');
      const category = token.getAttribute('data-mce-property-category');
      const subProperty = token.getAttribute('data-mce-subproperty');
      const defaultValue = token.getAttribute('data-mce-property-default-value');
      const filter = token.getAttribute('data-mce-filter');
      const placeholderNode = renderToken(personalizeConfig)({
        property,
        category,
        subProperty,
        defaultValue,
        filter
      });
      const parentNode = token.parentNode;
      if (parentNode && placeholderNode) {
        parentNode.replaceChild(placeholderNode, token);
      }
    });
  }
};

/**
 * Map plugin personalizeConfig to prop values for personalization browser
 *
 * @see PersonalizationBrowser.js
 */
export const pluginSettingsToProps = editor => {
  const personalizeConfig = getHsPersonalizeSettings(editor);
  if (!personalizeConfig) {
    return {};
  }
  return {
    isEmail: personalizeConfig.isEmail,
    hideFallbacks: personalizeConfig.hideFallbacks,
    hideSiteSettingsCategory: personalizeConfig.hideSiteSettingsCategory,
    includeInternalContactRecord: personalizeConfig.includeInternalContactRecord,
    includeSubscriptionTypeCategory: personalizeConfig.includeSubscriptionTypeCategory,
    allowEmptyFallbacks: personalizeConfig.allowEmptyFallbacks,
    supportedCrmObjectFQNs: personalizeConfig.supportedCrmObjectFQNs,
    customObjectPrefixes: personalizeConfig.customObjectPrefixes,
    useFlexibleAssociations: personalizeConfig.useFlexibleAssociations,
    useAsUpgradePoint: personalizeConfig.useAsUpgradePoint,
    dataTokenContext: personalizeConfig.dataTokenContext,
    unsupportedTokens: personalizeConfig.unsupportedTokens
  };
};